import platformUtils from '../privates/platformUtils';
import * as pm from '../privates/postMessage';
import {initValidationsService} from '../privates/validationsService/validationsService';
import compInitializeService from '../privates/compInitializeService/compInitializeService';
import {triggerOnReady, triggerOnRender, clearReadyManager} from '../privates/readyManager';
import appStudioUtils from '../privates/utils/appStudioUtils';
import {setShouldClearState} from '../privates/utils/livePreview';
import monitoring from '../privates/monitoring';
import {updateControllerData} from '../privates/utils/nativeComponentUtils';

function validateGlobalsParameters (platformUtils) {
    if (!platformUtils) {
        throw new Error(`invalid parameter value for platformUtils: ${platformUtils}`);
    }

    if (!platformUtils.RGI) {
        throw new Error(`invalid parameter value for remoteGlobalsInterface: ${platformUtils.RGI}`);
    }
}

function validateParameters (platformUtils) {
    if (!platformUtils) {
        throw new Error(`invalid parameter value for platformUtils: ${platformUtils}`);
    }

    if (!platformUtils.RMI) {
        throw new Error(`invalid parameter value for remoteModelInterface: ${platformUtils.RMI}`);
    }
}

function validateUtilitiesParameters (utilities) {
    if (!utilities) {
        throw new Error(`invalid parameter value for utils: ${utilities}`);
    }

    if (!utilities.richTextUtils) {
        throw new Error(`invalid parameter value for richTextUtils: ${utilities.richTextUtils}`);
    }

    if (!utilities.uriUtils) {
        throw new Error(`invalid parameter value for uriUtils: ${utilities.uriUtils}`);
    }

    if (!utilities.linkUtils) {
        throw new Error(`invalid parameter value for linkUtils: ${utilities.linkUtils}`);
    }

    if (!utilities.backgroundUtils) {
        throw new Error(`invalid parameter value for backgroundUtils: ${utilities.backgroundUtils}`);
    }

    if (!utilities.repeaterUtils) {
        throw new Error(`invalid parameter value for repeaterUtils: ${utilities.repeaterUtils}`);
    }

    if (!utilities.videoUrlsUtils) {
        throw new Error(`invalid parameter value for videoUrlsUtils: ${utilities.videoUrlsUtils}`);
    }

    if (!utilities.typeUtils) {
        throw new Error(`invalid parameter value for typeUtils: ${utilities.typeUtils}`);
    }

    if (!utilities.widgetUtils) {
        throw new Error(`invalid parameter value for widgetUtils: ${utilities.widgetUtils}`);
    }

    if (!utilities.mediaSrcHandler) {
        throw new Error(`invalid parameter value for mediaSrcHandler: ${utilities.mediaSrcHandler}`);
    }

    if (!utilities.pubSubService) {
        throw new Error(`invalid parameter value for pubSubService: ${utilities.pubSubService}`);
    }

    if (!utilities.messageService) {
        throw new Error(`invalid parameter value for messageService: ${utilities.messageService}`);
    }
}

function updateHandler(contextId) {
    return function delegateCommandToViewer({type, compId, data, appDefinitionId, controllerId, callback}) {
        switch (type) {
            case 'executeBatch':
            case 'registerEvent':
                pm.sendMessageToHost(pm.Intent.WIX_CODE, {command: type, compId, contextId, data, appDefinitionId, controllerId, callback});
                break;
            case 'behavior':
                pm.sendMessageToHost(pm.Intent.WIX_CODE, {command: 'executeBehavior', compId, contextId, data, appDefinitionId, controllerId, callback});
                break;
            default:
                pm.sendMessageToHost(pm.Intent.WIX_CODE, {command: `${type}Changed`, compId, contextId, data, appDefinitionId, controllerId, callback});
                break;
        }
    };
}

export default class InternalAPIs {
    dumpModel () {
        return {
            RMI: platformUtils.getRMI('dumpModel').toJson(),
            RGI: platformUtils.getRGI().toJson()
        };
    }

    initGlobals(platformUtilities) {
        validateGlobalsParameters(platformUtilities);
        platformUtils.initGlobals(platformUtilities);
        monitoring.setUserContext();
    }

    initUtilities(utilities){
        validateUtilitiesParameters(utilities);
        platformUtils.initUtilities(utilities);
    }

    initModel (...args) {
        validateParameters(...args);
        const [platformUtilities] = args;
        const contextId = platformUtils.getRGI().getContextId();
        const RMI = platformUtilities.RMI;
        const componentsHooks = platformUtilities.componentsHooks;
        const HOOKS = RMI.getHooks();
        RMI.registerHook(HOOKS.data, 'wysiwyg.viewer.components.Repeater', componentsHooks.repeaters.updateRepeaterItems);
        RMI.registerHook(HOOKS.all, null, componentsHooks.repeaters.syncRepeaterItemData);
        platformUtils.getWixSelector().initialize({currentRMI: RMI, contextId});
        platformUtils.init(platformUtilities, updateHandler(contextId));

        initValidationsService();
        compInitializeService.componentsInitialize(RMI, contextId, RMI.getCompToTypeMap());
    }

    setStaticEventHandlers (eventHandlers, contextId) {
        platformUtils.setStaticEventHandlers(eventHandlers, contextId);
    }

    getEventHandler(name) {
        return platformUtils.getStaticEventHandler(name, true);
    }

    onMessage(event) {
        pm.onMessage(event);
    }

    initEnv(queryParameters) {
        platformUtils.setQueryParameters(queryParameters);
    }

    addEnvParams(params) {
        platformUtils.addQueryParameters(params);
    }

    triggerOnReady(success) {
        triggerOnReady(success);
    }

    triggerOnRender(success) {
        triggerOnRender(success);
    }

    setWarmupData(...args) {
        const {controllerId, data, appDefinitionId} = Object.assign({}, ...args);
        if (controllerId) {
            pm.sendMessageToHost(pm.Intent.WIX_CODE_WARMUP, {
                command: pm.MessageTypes.SET_WARMUP_DATA,
                data: {controllerId, data},
                controllerId,
                appDefinitionId
            });
        } else {
            const _controllerId = args[0];
            const _data = args[1];
            pm.sendMessageToHost(pm.Intent.WIX_CODE_WARMUP, {
                command: pm.MessageTypes.SET_WARMUP_DATA,
                data: {controllerId: _controllerId, data: _data},
                controllerId: _controllerId
            });
        }
    }

    getWarmupData(controllerId) {
        const uw = platformUtils.getQueryParameter("userWarmup");
        return uw && uw[controllerId];
    }

    addAppStudioOnPropsChangedIfNeeded(){
        appStudioUtils.addAppStudioGlobalsIfNeeded();
    }

    addAppStudioGlobalsIfNeeded() {
        appStudioUtils.addAppStudioGlobalsIfNeeded();
    }

    updateControllerData(controllerId, data) {
        const currentRMI = platformUtils.getRMI();
        const contextId = platformUtils.getRGI().getContextId();
        updateControllerData(contextId, currentRMI, controllerId, data);
    }

    setBiServices(biServices) {
        platformUtils.setBiReporter(biServices);
    }

    clearReadyManager() {
        clearReadyManager();
    }

    addLivePreviewMode(livePreviewMode) {
        platformUtils.getRGI().addLivePreviewMode(livePreviewMode);
    }
    setShouldClearState(val){
        setShouldClearState(val);
    }
}
